import { Vec3 } from 'playcanvas';

import { CubicSpline } from './anim/spline';
import { AnimTrack } from './anim-track';
import { Events } from './events';

type Pose = {
    name: string,
    frame: number,
    position: Vec3,
    target: Vec3,
    fov?: number
};

/**
 * Camera animation track that manages camera keyframes and interpolation.
 * Implements AnimTrack interface so it can be used with the timeline system.
 *
 * Fully self-contained: subscribes to timeline events internally for
 * evaluation and spline rebuilding.
 */
class CameraAnimTrack implements AnimTrack {
    private poses: Pose[] = [];
    private events: Events;
    private onTimelineChange: ((frame: number) => void) | null = null;

    constructor(events: Events) {
        this.events = events;

        // Evaluate on timeline playback and scrub
        events.on('timeline.time', (time: number) => {
            this.evaluate(time);
        });

        events.on('timeline.frame', (frame: number) => {
            this.evaluate(frame);
        });

        // Rebuild spline when timeline parameters change
        events.on('timeline.frames', () => {
            this.rebuildSpline();
        });

        events.on('timeline.smoothness', () => {
            this.rebuildSpline();
        });

        // Clear track when scene is cleared
        events.on('scene.clear', () => {
            this.clear();
        });
    }

    get keys(): readonly number[] {
        return this.poses.map(p => p.frame);
    }

    addKey(frame: number): boolean {
        const pose = this.events.invoke('camera.getPose');
        if (!pose) return false;

        const existingIndex = this.poses.findIndex(p => p.frame === frame);

        const newPose: Pose = {
            name: `camera_${this.poses.length}`,
            frame,
            position: new Vec3(pose.position.x, pose.position.y, pose.position.z),
            target: new Vec3(pose.target.x, pose.target.y, pose.target.z),
            fov: pose.fov
        };

        if (existingIndex === -1) {
            this.poses.push(newPose);
            this.rebuildSpline();
            this.events.fire('track.keyAdded', frame);
        } else {
            this.poses[existingIndex] = newPose;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', frame);
        }
        return true;
    }

    removeKey(frame: number): boolean {
        const index = this.poses.findIndex(p => p.frame === frame);
        if (index === -1) return false;
        this.poses.splice(index, 1);
        this.rebuildSpline();
        this.events.fire('track.keyRemoved', frame);
        return true;
    }

    moveKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;

        const index = this.poses.findIndex(p => p.frame === fromFrame);
        if (index === -1) return false;

        // Remove any existing pose at the target frame
        const toIndex = this.poses.findIndex(p => p.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        // Update the frame (re-find index since splice may have shifted it)
        const movedIndex = this.poses.findIndex(p => p.frame === fromFrame);
        this.poses[movedIndex].frame = toFrame;
        this.rebuildSpline();
        this.events.fire('track.keyMoved', fromFrame, toFrame);
        return true;
    }

    copyKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;

        const source = this.poses.find(p => p.frame === fromFrame);
        if (!source) return false;

        // Remove any existing pose at the target frame
        const toIndex = this.poses.findIndex(p => p.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        this.poses.push({
            name: `camera_${this.poses.length}`,
            frame: toFrame,
            position: source.position.clone(),
            target: source.target.clone(),
            fov: source.fov
        });

        this.rebuildSpline();
        this.events.fire('track.keyAdded', toFrame);
        return true;
    }

    evaluate(frame: number): void {
        this.onTimelineChange?.(frame);
    }

    clear(): void {
        this.poses.length = 0;
        this.onTimelineChange = null;
        this.events.fire('track.keysCleared');
    }

    snapshot(): Pose[] {
        return this.poses.map(p => ({
            name: p.name,
            frame: p.frame,
            position: p.position.clone(),
            target: p.target.clone(),
            fov: p.fov
        }));
    }

    restore(snapshot: unknown): void {
        this.poses = (snapshot as Pose[]).map(p => ({
            name: p.name,
            frame: p.frame,
            position: p.position.clone(),
            target: p.target.clone(),
            fov: p.fov
        }));
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    /**
     * Add a pose directly (used for deserialization and legacy import).
     */
    addPose(pose: Pose): void {
        if (pose.frame === undefined) {
            return;
        }

        pose.fov ??= this.events.invoke('camera.fov') ?? 60;

        const idx = this.poses.findIndex(p => p.frame === pose.frame);
        if (idx !== -1) {
            this.poses[idx] = pose;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', pose.frame);
        } else {
            this.poses.push(pose);
            this.rebuildSpline();
            this.events.fire('track.keyAdded', pose.frame);
        }
    }

    /**
     * Get all poses (used for serialization and legacy consumers).
     */
    getPoses(): readonly Pose[] {
        return this.poses;
    }

    /**
     * Load poses from serialized data.
     */
    loadPoses(posesData: Pose[]): void {
        this.poses.length = 0;
        posesData.forEach((pose) => {
            this.poses.push(pose);
        });
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    private rebuildSpline(): void {
        const duration = this.events.invoke('timeline.frames');
        const smoothness = this.events.invoke('timeline.smoothness');

        const orderedPoses = this.poses.slice()
        .filter(a => a.frame < duration)
        .sort((a, b) => a.frame - b.frame);

        const times = orderedPoses.map(p => p.frame);
        const points: number[] = [];
        for (let i = 0; i < orderedPoses.length; ++i) {
            const p = orderedPoses[i];
            points.push(p.position.x, p.position.y, p.position.z);
            points.push(p.target.x, p.target.y, p.target.z);
            points.push(p.fov);
        }

        if (orderedPoses.length > 1) {
            const spline = CubicSpline.fromPointsLooping(duration, times, points, smoothness);
            const result: number[] = [];
            const pose = { position: new Vec3(), target: new Vec3(), fov: 0 };

            this.onTimelineChange = (frame: number) => {
                spline.evaluate(frame, result);
                pose.position.set(result[0], result[1], result[2]);
                pose.target.set(result[3], result[4], result[5]);
                pose.fov = result[6];
                this.events.fire('camera.setPose', pose, 0);
            };
        } else {
            this.onTimelineChange = null;
        }

        // re-evaluate at the current frame so the camera updates immediately
        this.evaluate(this.events.invoke('timeline.frame'));
    }
}

/**
 * Register the camera animation track and expose it via events.
 * The track is fully self-contained (subscribes to timeline events internally),
 * so this function only needs to create it, expose it, and handle serialization.
 */
const registerCameraPosesEvents = (events: Events) => {
    const track = new CameraAnimTrack(events);

    // Expose the camera animation track
    events.function('camera.animTrack', () => {
        return track;
    });

    // Legacy support: expose poses
    events.function('camera.poses', () => {
        return track.getPoses();
    });

    // Legacy support: add pose directly
    events.on('camera.addPose', (pose: Pose) => {
        track.addPose(pose);
    });

    /** Volugraph: clear keyframed camera path without touching scene */
    events.on('volugraph.clearCameraAnimTrack', () => {
        track.clear();
    });

    /**
     * Volugraph: build a flythrough from capture annotations (position + cameraView only).
     * @param raw — array of { title?: string, cameraView?: { position: number[], target: number[] } }
     */
    events.on('volugraph.importAnnotationKeyframes', (raw: unknown) => {
        if (!Array.isArray(raw) || raw.length === 0) {
            return;
        }
        const fps = (events.invoke('timeline.frameRate') as number) || 30;
        const spacing = Math.max(1, Math.floor(fps * 3));
        const defaultFov = events.invoke('camera.fov') ?? 60;

        const poses: Pose[] = [];
        for (let i = 0; i < raw.length; i++) {
            const ann = raw[i] as { title?: string; cameraView?: { position: number[]; target: number[] } };
            const cv = ann?.cameraView;
            if (!cv || !Array.isArray(cv.position) || !Array.isArray(cv.target)) {
                continue;
            }
            if (cv.position.length !== 3 || cv.target.length !== 3) {
                continue;
            }
            const title = typeof ann.title === 'string' && ann.title.trim() ? ann.title.trim() : `Keyframe_${i + 1}`;
            poses.push({
                name: title,
                frame: i * spacing,
                position: new Vec3(cv.position[0], cv.position[1], cv.position[2]),
                target: new Vec3(cv.target[0], cv.target[1], cv.target[2]),
                fov: defaultFov
            });
        }
        if (poses.length === 0) {
            return;
        }
        const lastFrame = poses[poses.length - 1]!.frame;
        const duration = Math.max(lastFrame + spacing, lastFrame + 1);
        events.fire('timeline.setFrames', duration);
        track.loadPoses(poses);
        events.fire('timeline.setFrame', 0);
    });

    /** Volugraph: serialize timeline + camera keyframes for Firestore persistence */
    events.function('volugraph.exportAnimation', () => {
        const poses = track.getPoses();
        return {
            timeline: {
                frames: events.invoke('timeline.frames') as number,
                frameRate: events.invoke('timeline.frameRate') as number,
                smoothness: events.invoke('timeline.smoothness') as number
            },
            keyframes: poses.map((p) => ({
                name: p.name,
                frame: p.frame,
                position: [p.position.x, p.position.y, p.position.z],
                target: [p.target.x, p.target.y, p.target.z],
                fov: p.fov
            }))
        };
    });

    /**
     * Volugraph: restore persisted animation (timeline settings + keyframes).
     * @param data — { timeline?: { frames, frameRate, smoothness }, keyframes?: [...] }
     */
    events.on('volugraph.importAnimation', (data: unknown) => {
        if (!data || typeof data !== 'object') {
            return;
        }
        const d = data as {
            timeline?: { frames?: number; frameRate?: number; smoothness?: number };
            keyframes?: Array<{
                name?: string;
                frame?: number;
                position?: number[];
                target?: number[];
                fov?: number;
            }>;
        };
        const tl = d.timeline;
        if (tl) {
            if (typeof tl.frames === 'number' && Number.isFinite(tl.frames)) {
                events.fire('timeline.setFrames', Math.max(1, Math.floor(tl.frames)));
            }
            if (typeof tl.frameRate === 'number' && Number.isFinite(tl.frameRate)) {
                events.fire('timeline.setFrameRate', tl.frameRate);
            }
            if (typeof tl.smoothness === 'number' && Number.isFinite(tl.smoothness)) {
                events.fire('timeline.setSmoothness', Math.max(0, Math.min(1, tl.smoothness)));
            }
        }
        const defaultFov = events.invoke('camera.fov') ?? 60;
        if (Array.isArray(d.keyframes) && d.keyframes.length > 0) {
            const loaded: Pose[] = [];
            for (let i = 0; i < d.keyframes.length; i++) {
                const k = d.keyframes[i];
                if (!k || !Array.isArray(k.position) || !Array.isArray(k.target)) {
                    continue;
                }
                if (k.position.length !== 3 || k.target.length !== 3) {
                    continue;
                }
                loaded.push({
                    name: typeof k.name === 'string' && k.name.trim() ? k.name.trim() : `Keyframe_${i + 1}`,
                    frame: typeof k.frame === 'number' && Number.isFinite(k.frame) ? Math.max(0, Math.floor(k.frame)) : 0,
                    position: new Vec3(k.position[0], k.position[1], k.position[2]),
                    target: new Vec3(k.target[0], k.target[1], k.target[2]),
                    fov: typeof k.fov === 'number' && Number.isFinite(k.fov) ? k.fov : defaultFov
                });
            }
            if (loaded.length > 0) {
                track.loadPoses(loaded);
            } else {
                track.clear();
            }
        } else {
            track.clear();
        }
        events.fire('timeline.setPlaying', false);
        events.fire('timeline.setFrame', 0);
    });

    /**
     * Volugraph: generate a 360° orbit flythrough from the current camera distance around scene bounds.
     * @param opts — { keyCount?: number, durationSeconds?: number }
     */
    events.on('volugraph.generateOrbitKeyframes', (opts: unknown) => {
        const scene = (window as { scene?: { bound: { center: Vec3; halfExtents: Vec3 } } }).scene;
        if (!scene?.bound) {
            return;
        }
        const o = (opts && typeof opts === 'object' ? opts : {}) as { keyCount?: number; durationSeconds?: number };
        const keyCount = Math.max(4, Math.min(48, Math.floor(o.keyCount ?? 24)));
        const fps = (events.invoke('timeline.frameRate') as number) || 30;
        const durationSeconds = Math.max(3, Math.min(120, Number(o.durationSeconds) || 8));
        const duration = Math.max(keyCount, Math.floor(fps * durationSeconds));

        const center = scene.bound.center.clone();
        const pose = events.invoke('camera.getPose') as { position: Vec3; target: Vec3; fov?: number } | null;
        if (!pose?.position) {
            return;
        }
        const offset = new Vec3().sub2(pose.position, center);
        const radiusXZ = Math.max(
            scene.bound.halfExtents.length() * 1.35,
            Math.sqrt(offset.x * offset.x + offset.z * offset.z) || 1
        );
        const height = offset.y;
        const defaultFov = pose.fov ?? (events.invoke('camera.fov') as number) ?? 60;

        const poses: Pose[] = [];
        for (let i = 0; i < keyCount; i++) {
            const angle = (i / keyCount) * Math.PI * 2;
            poses.push({
                name: `orbit_${i + 1}`,
                frame: Math.floor((i / keyCount) * duration),
                position: new Vec3(
                    center.x + radiusXZ * Math.sin(angle),
                    center.y + height,
                    center.z + radiusXZ * Math.cos(angle)
                ),
                target: center.clone(),
                fov: defaultFov
            });
        }
        events.fire('timeline.setFrames', duration);
        track.loadPoses(poses);
        events.fire('timeline.setPlaying', false);
        events.fire('timeline.setFrame', 0);
    });

    // Serialization

    events.function('docSerialize.poseSets', (): any[] => {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];
        const poses = track.getPoses();

        if (poses.length === 0) {
            return [];
        }

        return [{
            name: 'set0',
            poses: poses.map((pose) => {
                return {
                    name: pose.name,
                    frame: pose.frame,
                    position: pack3(pose.position),
                    target: pack3(pose.target),
                    fov: pose.fov
                };
            })
        }];
    });

    events.function('docDeserialize.poseSets', (poseSets: any[], documentCameraFov?: number) => {
        if (!poseSets || poseSets.length === 0) {
            return;
        }

        const fps = events.invoke('timeline.frameRate');

        const defaultFov = documentCameraFov ?? events.invoke('camera.fov') ?? 60;

        const loadedPoses: Pose[] = poseSets[0].poses.map((docPose: any, index: number) => {
            return {
                name: docPose.name,
                frame: docPose.frame ?? (index * fps),
                position: new Vec3(docPose.position),
                target: new Vec3(docPose.target),
                fov: docPose.fov ?? defaultFov
            };
        });

        track.loadPoses(loadedPoses);
    });
};

export { registerCameraPosesEvents, CameraAnimTrack, Pose };
